import type {
  AugmentedWarViewerState,
  CellId,
  Element,
  PlayerId,
} from "@cloudnest/augmented-war-engine";
import {
  CELL_IDS,
  countUnitsOnBoard,
  ELEMENTS,
  isHomeOf,
  neighbors,
  totalHpOnBoard,
} from "@cloudnest/augmented-war-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { Board, combatGlyph } from "./components/Board";
import {
  augmentedWarMultiplayerActions,
  useAugmentedWarMultiplayerStore,
} from "./multiplayer-game-store";

const who = (p: string): string => (p === "p1" ? "P1" : "P2");
const opponentSlot = (p: PlayerId): PlayerId => (p === "p1" ? "p2" : "p1");

/** Adjacent on-board cells (engine adjacency), split by move/attack legality. */
function adjacentTargets(
  state: AugmentedWarViewerState,
  from: CellId,
  mySlot: PlayerId,
): {
  readonly moves: ReadonlySet<CellId>;
  readonly attacks: ReadonlySet<CellId>;
} {
  const moves = new Set<CellId>();
  const attacks = new Set<CellId>();
  for (const n of neighbors(from)) {
    const unit = state.board[n];
    if (!unit) moves.add(n);
    else if (unit.owner === opponentSlot(mySlot)) attacks.add(n);
  }
  return { moves, attacks };
}

function ElementPicker({
  spent,
  onPick,
  testIdPrefix,
}: {
  readonly spent: readonly Element[];
  readonly onPick: (element: Element) => void;
  readonly testIdPrefix: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {ELEMENTS.map((element) => {
        const onCooldown = spent.includes(element);
        return (
          <button
            key={element}
            type="button"
            disabled={onCooldown}
            data-testid={`${testIdPrefix}-${element}`}
            onClick={() => onPick(element)}
            className="min-w-11 rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-lg disabled:opacity-30"
          >
            {combatGlyph[element]}
          </button>
        );
      })}
    </div>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, mySlot } = useAugmentedWarMultiplayerStore();
  const actions = augmentedWarMultiplayerActions;

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const participantIds = useMemo(
    () => currentRoom?.participantUserIds ?? [],
    [currentRoom?.participantUserIds],
  );
  const isObserver = Boolean(user) && !participantIds.includes(user?.id ?? "");

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useGameEventsWS(roomId, {
    onGameEvent: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id + role are the stable init boundaries.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    if (isObserver) {
      void actions.initializeAsObserver(roomId);
    } else if (participantIds.length === 2) {
      if (isHost) void actions.initializeAsHost(roomId);
      else void actions.initializeAsGuest(roomId);
    }
    return () => {
      actions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost, isObserver]);

  // ── local interaction state ──────────────────────────────────────────────
  const [selectedCell, setSelectedCell] = useState<CellId | null>(null);
  const [deployMode, setDeployMode] = useState(false);
  const [attackTarget, setAttackTarget] = useState<CellId | null>(null);

  const pending = viewerState?.pendingAttack ?? null;
  // Reset transient selection whenever the server-projected turn boundary moves.
  const turnKey = `${viewerState?.turn ?? 0}:${viewerState?.currentPlayerId ?? "-"}:${pending ? "pend" : "open"}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on the turn boundary only.
  useEffect(() => {
    setSelectedCell(null);
    setDeployMode(false);
    setAttackTarget(null);
  }, [turnKey]);

  if (!currentRoom) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">방을 찾을 수 없습니다</h1>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            로비로
          </button>
        </section>
      </div>
    );
  }

  if (!viewerState) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface-primary text-text-secondary"
        data-testid="augmented-war-game-loading"
      >
        연결 중…
      </div>
    );
  }

  const state = viewerState;
  const isGameOver = state.phase === "GAME_END";
  const iAmDefender =
    pending !== null && mySlot !== null && pending.attacker !== mySlot;
  const iAmAttackerWaiting = pending !== null && pending.attacker === mySlot;
  const isMyTurn =
    !pending &&
    !isGameOver &&
    mySlot !== null &&
    state.currentPlayerId === mySlot;
  const mySpent = mySlot ? state.players[mySlot].spentElements : [];

  // ── selectable cells + click routing ─────────────────────────────────────
  let selectable: ReadonlySet<CellId> = new Set();
  let onCellClick: ((id: CellId) => void) | undefined;
  const targets =
    isMyTurn && mySlot && selectedCell
      ? adjacentTargets(state, selectedCell, mySlot)
      : { moves: new Set<CellId>(), attacks: new Set<CellId>() };

  if (isMyTurn && mySlot && !attackTarget) {
    if (deployMode) {
      selectable = new Set(myEmptyHomes(state, mySlot));
      onCellClick = (id) => {
        if (selectable.has(id)) {
          void actions.deploy(id);
          setDeployMode(false);
        }
      };
    } else if (selectedCell) {
      selectable = new Set<CellId>([
        selectedCell,
        ...targets.moves,
        ...targets.attacks,
        ...myUnits(state, mySlot),
      ]);
      onCellClick = (id) => {
        if (id === selectedCell) {
          setSelectedCell(null);
        } else if (state.board[id]?.owner === mySlot) {
          setSelectedCell(id);
        } else if (targets.moves.has(id)) {
          void actions.move(selectedCell, id);
          setSelectedCell(null);
        } else if (targets.attacks.has(id)) {
          setAttackTarget(id);
        }
      };
    } else {
      selectable = new Set(myUnits(state, mySlot));
      onCellClick = (id) => {
        if (state.board[id]?.owner === mySlot) setSelectedCell(id);
      };
    }
  }

  const dangerCell = iAmDefender ? (pending?.target ?? null) : null;

  return (
    <div
      className="min-h-screen bg-surface-primary text-text-primary"
      data-testid="augmented-war-game-screen"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:flex-row">
        <div className="min-w-0 flex-1 rounded-md border border-border-primary bg-surface-secondary p-2">
          <Board
            state={state}
            testId="augmented-war-game-board"
            onCellClick={onCellClick}
            selectableCells={selectable}
            selectedCell={selectedCell}
            dangerCell={dangerCell}
          />
        </div>

        <aside className="flex flex-col gap-3 md:w-72">
          <div
            className="rounded-md border border-border-primary bg-surface-secondary p-3 text-center font-semibold text-sm"
            data-testid="augmented-war-turn-banner"
          >
            {isGameOver
              ? state.result
                ? `게임 종료 · ${state.result.winner ? `${who(state.result.winner)} 승` : "무승부"}`
                : "게임 종료"
              : iAmDefender
                ? "⚔ 방어! 방어 원소를 선택하세요"
                : iAmAttackerWaiting
                  ? "상대의 방어를 기다리는 중…"
                  : isMyTurn
                    ? `내 차례 · AP ${state.apRemaining}`
                    : isObserver
                      ? `${who(state.currentPlayerId)} 차례 (관전)`
                      : "상대 차례"}
          </div>

          {(["p1", "p2"] as const).map((p) => (
            <div
              key={p}
              className={`rounded-md border p-3 text-sm ${
                state.currentPlayerId === p && !isGameOver
                  ? "border-accent-blue"
                  : "border-border-primary"
              } bg-surface-secondary`}
            >
              <div className="font-semibold">
                {who(p)}
                {p === mySlot ? " (나)" : ""} · {state.players[p].faction}
              </div>
              <div className="text-text-secondary text-xs">
                reserve {state.players[p].reserve} · on board{" "}
                {countUnitsOnBoard(state.board, p)} · HP{" "}
                {totalHpOnBoard(state.board, p)}
              </div>
            </div>
          ))}

          {/* Contextual action bar */}
          <div className="rounded-md border border-border-primary bg-surface-secondary p-3">
            {iAmDefender ? (
              <div className="flex flex-col gap-2">
                <div className="text-text-secondary text-xs">
                  {pending
                    ? `${pending.from} → ${pending.target} 공격받는 중`
                    : ""}
                </div>
                <ElementPicker
                  spent={mySpent}
                  onPick={(element) => void actions.resolveAttack(element)}
                  testIdPrefix="augmented-war-defend"
                />
              </div>
            ) : attackTarget && selectedCell ? (
              <div className="flex flex-col gap-2">
                <div className="text-text-secondary text-xs">
                  {selectedCell} → {attackTarget} 공격 · 원소 선택
                </div>
                <ElementPicker
                  spent={mySpent}
                  onPick={(element) => {
                    void actions.declareAttack(
                      selectedCell,
                      attackTarget,
                      element,
                    );
                    setSelectedCell(null);
                    setAttackTarget(null);
                  }}
                  testIdPrefix="augmented-war-attack"
                />
                <button
                  type="button"
                  className="self-start rounded-md border border-border-primary px-3 py-1 text-sm"
                  onClick={() => setAttackTarget(null)}
                >
                  취소
                </button>
              </div>
            ) : isMyTurn ? (
              <div className="flex flex-col gap-2">
                <div className="text-text-secondary text-xs">
                  {deployMode
                    ? "배치할 홈 칸을 선택"
                    : selectedCell
                      ? "이동할 빈 칸 또는 공격할 적을 선택"
                      : "내 유닛을 선택하세요"}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="augmented-war-deploy-toggle"
                    className={`rounded-md border px-3 py-2 font-semibold text-sm ${
                      deployMode
                        ? "border-accent-blue text-accent-blue"
                        : "border-border-primary"
                    }`}
                    onClick={() => {
                      setSelectedCell(null);
                      setDeployMode((v) => !v);
                    }}
                  >
                    {deployMode ? "배치 취소" : "배치"}
                  </button>
                  {selectedCell && !deployMode ? (
                    <button
                      type="button"
                      className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm"
                      onClick={() => setSelectedCell(null)}
                    >
                      선택 해제
                    </button>
                  ) : null}
                  <button
                    type="button"
                    data-testid="augmented-war-endturn"
                    className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white"
                    onClick={() => void actions.endTurn()}
                  >
                    턴 종료
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-text-secondary">
                {isGameOver
                  ? "게임이 종료되었습니다."
                  : "상대의 행동을 기다리는 중…"}
              </div>
            )}
          </div>

          {lastError ? (
            <div
              className="rounded-md border border-accent-red bg-surface-secondary p-2 text-accent-red text-xs"
              data-testid="augmented-war-error"
            >
              {lastError.error}
              <button
                type="button"
                className="ml-2 underline"
                onClick={() => actions.clearError()}
              >
                닫기
              </button>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

// ── small pure helpers over the projected state ────────────────────────────
function myUnits(
  state: AugmentedWarViewerState,
  mySlot: PlayerId,
): readonly CellId[] {
  return Object.keys(state.board).filter(
    (id) => state.board[id]?.owner === mySlot,
  );
}

function myEmptyHomes(
  state: AugmentedWarViewerState,
  mySlot: PlayerId,
): readonly CellId[] {
  return CELL_IDS.filter((id) => isHomeOf(id, mySlot) && !state.board[id]);
}
